import { defineComponent as _defineComponent } from 'vue'
import {computed, getCurrentInstance, inject, onUnmounted, ref, watch} from 'vue';
import {Utils} from '@/services/utils/BasicUtils';
import {UserLocalStorage} from '@/services/UserLocalStorage';
import {resolveDataTest} from '@/components/fpComponents/useDataTest';

interface FpChipRadioPropsValidation {
  readonly required: boolean;
}

type FpChipRadioItem = Record<string, any>;

interface FpChipRadioProps {
  // Must stay `unknown` - a narrower type emits a runtime type-check and Vue warns on the `null` emitted on deselect.
  value: unknown;
  items?: FpChipRadioItem[];
  required?: boolean;
  multiple?: boolean;
  label?: string;
  itemKey?: string;
  itemName?: string;
  itemIcon?: string;
  itemCount?: string;
  prependIcon?: string;
  itemColor?: string;
  localStorageKey?: string;
  id?: string;
  dataTest?: string;
  mandatory?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FpChipRadio',
  props: {
    value: null,
    items: { default: () => ([
    {
      type: true,
      name: 'Tak',
    },
    {
      type: false,
      name: 'Nie',
    },
  ]) },
    required: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    label: null,
    itemKey: { default: 'type' },
    itemName: { default: 'name' },
    itemIcon: { default: 'icon' },
    itemCount: { default: 'count' },
    prependIcon: null,
    itemColor: { default: 'color' },
    localStorageKey: null,
    id: null,
    dataTest: null,
    mandatory: { type: Boolean, default: false }
  },
  emits: ["update:value", "input"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'update:value', value: any): void;
  (e: 'input', value: any): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: unknown, items: FpChipRadioItem[], required: boolean, multiple: boolean, label?: string, itemKey: string, itemName: string, itemIcon: string, itemCount: string, prependIcon?: string, itemColor: string, localStorageKey?: string, id?: string, dataTest?: string, mandatory: boolean };





const instance = getCurrentInstance()!;
const form = inject<Nullable<IForm>>('form', null);

const resolvedDataTest = computed<string | undefined>(() => resolveDataTest(props.dataTest, props.id));

const hasError = ref<boolean>(false);
const rules: ValidationRules<FpChipRadioPropsValidation, unknown> = {
  required: (v) => Array.isArray(v) ? v.length > 0 : !!v,
};

const resetValidation = (): void => {
  hasError.value = false;
};

const validate = (): boolean => {
  hasError.value = Utils.keys<FpChipRadioPropsValidation>(rules)
    .map((key) => (props[key] === false || props[key] === undefined) || rules[key]!(props.value))
    .includes(false);
  return !hasError.value;
};

const chipColor = (active: boolean, item: FpChipRadioItem): string => hasError.value
  ? 'var(--token-error-base)'
  : active
    ? item[props.itemColor] || 'var(--token-primary-text)'
    : '';

// `color(from …)` so the tint also works for `var(--token-…)` item colours, which a
// hex-appended alpha silently breaks.
const chipBackgroundColor = (active: boolean, item: FpChipRadioItem): string => hasError.value
  ? 'var(--token-error-base)'
  : active
    ? `color(from ${item[props.itemColor] || 'var(--token-primary-text)'} srgb r g b / 0.21)`
    : '';

const updateSelectedTags = (value: unknown): void => {
  if (!props.multiple && !props.mandatory && value === props.value) {
    value = null;
  }
  if (props.value !== value) {
    // Order matters: .sync must write the model before the @input listeners run.
    emit('update:value', value);
    emit('input', value);
  }
  if (props.localStorageKey) {
    UserLocalStorage.setItem(props.localStorageKey, typeof value === 'object' ? JSON.stringify(value) : value as string);
  }
};

const onClick = (tag?: unknown): void => {
  resetValidation();
  if (tag !== undefined) {
    if (props.multiple) {
      if (Array.isArray(props.value)) {
        if (!props.value.includes(tag)) {
          props.value.push(tag);
          updateSelectedTags(props.value);
        } else {
          updateSelectedTags(props.value.filter(x => x !== tag));
        }
      } else {
        updateSelectedTags([tag,]);
      }
    } else {
      updateSelectedTags(tag);
    }
  } else {
    console.error('Tag is invalid', tag);
    if (props.items[0] && !props.items[0][props.itemKey]) {
      console.error('itemKey is invalid', props.itemKey);
    }
  }
};

watch(() => props.required, () => {
  resetValidation();
});

expose({form, validate, resetValidation,});

if (form) {
  form.register(instance.proxy);
}

onUnmounted(() => {
  if (form) {
    form.unregister(instance.proxy);
  }
});

return { __sfc: true,props, emit, instance, form, resolvedDataTest, hasError, rules, resetValidation, validate, chipColor, chipBackgroundColor, updateSelectedTags, onClick }
}

})